import type { ReactElement, ReactNode } from "react";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";

import { AuthBackdrop } from "@/components/layout/AuthBackdrop";
import { LanguageToggle } from "@/components/layout/LanguageToggle";
import { LogoMark } from "@/components/layout/Sidebar";
import ThemeToggle from "@/components/layout/ThemeToggle";
import { UserAccountMenu } from "@/components/layout/UserAccountMenu";
import { Heading } from "@/components/ui/heading";
import { cn } from "@/lib/utils";

type AuthPageShellProps = {
  children: ReactNode;
  /** Show account menu (org switcher, theme, language) instead of standalone toggles. */
  showAccountMenu?: boolean;
  /** Extra header controls (e.g. Sair) next to theme/language — not the profile menu. */
  headerActions?: ReactNode;
  /** Grid and sketches. Off for session-loading gates so they do not flash on every app load. */
  showBackdrop?: boolean;
  /** `missing` uses smaller broken sketches on 404. */
  backdropVariant?: "default" | "missing";
  /** `start` keeps the top of tall legal pages in view. Auth cards stay centered. */
  mainAlign?: "center" | "start";
};

export function AuthPageShell({
  children,
  showAccountMenu = false,
  headerActions,
  showBackdrop = true,
  backdropVariant = "default",
  mainAlign = "center",
}: AuthPageShellProps): ReactElement {
  return (
    <div className="fixed inset-0 flex flex-col overflow-hidden bg-background text-foreground">
      {showBackdrop ? <AuthBackdrop variant={backdropVariant} /> : null}
      <header className="relative z-10 flex shrink-0 items-center justify-between gap-3 px-4 py-6 sm:px-8">
        <div className="flex items-center gap-2.5">
          <LogoMark />
          <Heading variant="section" as="span">
            KonstruLog
          </Heading>
        </div>
        <div className="flex shrink-0 items-center gap-0.5">
          {showAccountMenu ? (
            <UserAccountMenu variant="header" />
          ) : (
            <>
              <ThemeToggle />
              <LanguageToggle />
              {headerActions}
            </>
          )}
        </div>
      </header>

      <main
        className={cn(
          "relative z-10 flex min-h-0 flex-1 flex-col items-center overflow-y-auto px-4 py-6 sm:px-8",
          mainAlign === "start" ? "justify-start" : "justify-center",
        )}
      >
        {children}
      </main>
    </div>
  );
}

/** Full-viewport spinner for session restore — no KonstruLog chrome (that stays on login/register). */
export function AuthPageLoading(): ReactElement {
  const { t } = useTranslation();
  return (
    <div className="fixed inset-0 flex items-center justify-center bg-background text-foreground">
      <Loader2 className="size-8 animate-spin text-primary" aria-hidden />
      <span className="sr-only">{t("common.loading")}</span>
    </div>
  );
}
